import {
  DeleteOutlined,
  FileSyncOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Button, Input, Modal, Select } from "antd";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { onError } from "src/common/onError";
import { MdmTable } from "src/components/MdmTable";
import { PageLayout } from "src/components/PageLayout";
import {
  ColumnFiltersDict,
  multiSelectColumnFilter,
  textColumnFilter,
} from "src/components/ReactiveTable/components/AntDropdownFilter";
import { RowClickProps } from "src/components/ReactiveTable/components/ReactiveTable/componentProps";
import { RtColumnsSettingsButton } from "src/components/ReactiveTable/components/RtColumnsSettings";
import { RtSelectionType } from "src/components/ReactiveTable/RtTypes";
import { ZDemoData } from "src/pages/tests/TableDemoPage/data/demoDataTypes";
import { makeUrl } from "src/routes/makeUrl";
import { PageUrl } from "src/routes/PageUrl";

import { countryOptions } from "./data/demoDataGenerator";
import { demoColumns } from "./demoColumns";
import * as styles from "./TableDemoPage.module.less";
import { tableDemoPageStore as store } from "./TableDemoPageStore";

export const TableDemoPage: React.FC = observer(() => {
  const [modal, contextHolder] = Modal.useModal();
  const { tableStore } = store;

  // Пример обработки клика на строке
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const clickHandler: RowClickProps<ZDemoData> = {
    type: "click",
    handler: (row) => {
      modal.info({
        content: `Клик по строке: ${row.name}`,
      });
    },
  };

  // Пример назначения ссылки на строку
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const linkHandler: RowClickProps<ZDemoData> = {
    type: "link",
    builder: () => makeUrl(PageUrl.test),
  };

  const columnFilters: ColumnFiltersDict = {
    name: textColumnFilter(
      () => tableStore.filters?.name,
      (text) => tableStore.changeSingleFilter("name", text || undefined),
    ),
    country: multiSelectColumnFilter({
      getValues: () => tableStore.filters?.countryCodes,
      setValues: (values: string[] | undefined) =>
        tableStore.changeSingleFilter("countryCodes", values),
      options: countryOptions,
    }),
  };

  return (
    <PageLayout title="Table Demo">
      <div className={styles.page}>
        <div className={styles.tools}>
          <Button
            size="small"
            onClick={() => store.reload()}
            disabled={store.buzy}
            icon={<ReloadOutlined />}
          >
            Reload
          </Button>
          <Button
            size="small"
            disabled={store.isDisabled("generate")}
            loading={store.mode === "generate"}
            icon={<FileSyncOutlined />}
            onClick={() =>
              modal.confirm({
                type: "warning",
                title: "Внимание!",
                content: "Текущие данные будут заменены на новые",
                okText: "Генерировать",
                onOk: () => {
                  store.generate().catch(onError);
                },
              })
            }
          >
            Generate
          </Button>
          <Button
            size="small"
            disabled={store.isDisabled("deleteAll")}
            loading={store.mode === "deleteAll"}
            icon={<DeleteOutlined />}
            onClick={() => {
              modal.confirm({
                type: "warning",
                title: "Внимание!",
                content: "Все данные будут удалены!",
                okText: "Удалить!",
                okButtonProps: { danger: true },
                onOk: () => {
                  store.deleteAll().catch(onError);
                },
              });
            }}
          >
            Clear
          </Button>
          <Select
            size="small"
            options={store.selectionOptions}
            value={tableStore.selectionType || ""}
            onChange={(v) => {
              tableStore.setSelectedKeys([]);
              tableStore.setSelectionType((v as RtSelectionType) || null);
            }}
          />
          <RtColumnsSettingsButton
            size="small"
            store={tableStore}
            tooltip="Настройка столбцов"
          />
        </div>
        <div className={styles.filters}>
          <Input
            size="small"
            placeholder="Name"
            allowClear
            value={tableStore.filters?.name}
            // TODO: контролировать отдельно каждый фильтр плохо, т.к. нужен debounce. Поэтому лучше в виде формы
            onChange={(e) =>
              tableStore.changeFilters({
                ...tableStore.filters,
                name: e.currentTarget.value,
              })
            }
          />
        </div>
        <div className={styles.content}>
          <MdmTable
            verticalMode="fit"
            store={store.tableStore}
            columns={demoColumns}
            showTotal={(count) => `Всего строк: ${count}`}
            columnFilters={columnFilters}
            resizableColumns
          />
        </div>
      </div>
      {contextHolder}
    </PageLayout>
  );
});
